import React, { useState, useEffect, useRef } from 'react';
import ModalPage from 'components/explore/layout/ModalPage';
import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  Tooltip,
  Button,
  TextField,
  IconButton,
  Collapse,
  CircularProgress,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import {
  Paging,
  Column,
  HeaderFilter,
  SearchPanel,
} from 'devextreme-react/data-grid';
import DataGrid from 'components/devExtreme/DataGrid';
import { Constants } from 'utilities/constants';
import {
  fetchAssumptionModules,
  createAssumptionModule,
  deleteAssumptionModule,
  setConfirmationModalState,
} from 'store/actions';

const pageSize = 10;

function ModuleManagerGrid({
  classes,
  user,
  modules,
  fetchAssumptionModules,
  createAssumptionModule,
  deleteAssumptionModule,
  modalOpen,
  shouldCollapseGrid,
  modulesAreLoading,
  onClose,
  setConfirmationModalState,
}) {
  // == CONSTANTS =============================================

  const gridRef = useRef(null);

  // == STATE =================================================

  const [moduleNameInput, setModuleNameInput] = useState('');
  const [moduleNameError, setModuleNameError] = useState('');
  const [moduleDescriptionInput, setModuleDescriptionInput] = useState('');
  const [saveModuleExpanded, setSaveModuleExpanded] = useState(false);
  const [gridExpanded, setGridExpanded] = useState(true);
  const descriptionExceedsLength = moduleDescriptionInput.length > 100;
  // == EFFECTS ===============================================

  useEffect(() => {
    if (modalOpen) {
      fetchAssumptionModules();

      if (shouldCollapseGrid) {
        setSaveModuleExpanded(true);
        setGridExpanded(false);
      } else {
        setGridExpanded(true);
      }
    }
  }, [modalOpen, shouldCollapseGrid]);

  // == EVENT HANDLERS =======================================

  const setDefaults = () => {
    setModuleNameInput('');
    setModuleDescriptionInput('');
  };

  const handleModuleDelete = (e) => {
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => {
        const { moduleId } = e.data;
        closeConfirmationModal();
        deleteAssumptionModule(moduleId);
      },
      handleCancellation: () => {
        closeConfirmationModal();
      },
      titleText: 'Confirm',
      contentText: `Are you sure you want to delete this Module?`,
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  };

  const handleModuleNameInputChange = (e) => {
    if (moduleNameError?.length) {
      setModuleNameError('');
    }

    setModuleNameInput(e.target.value);
  };

  const handleModuleDescriptionInputChange = (e) => {
    setModuleDescriptionInput(e.target.value);
  };

  const onCreateNewModuleClick = () => {
    if (moduleNameInput === '') {
      setModuleNameError('Name is required');
      return;
    }

    createAssumptionModule({
      name: moduleNameInput,
      description: moduleDescriptionInput,
    });

    setDefaults();
    onClose();
  };

  // == COMPONENTS ==========================================

  const deleteColumnRender = (e) => {
    if (e.data.canDelete === false) return null;
    return (
      <Tooltip title={'Delete'} placement='bottom'>
        <IconButton
          style={{
            padding: 2,
            margin: -2,
          }}
          onClick={() => handleModuleDelete(e)}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <ModalPage
      maxWidth='md'
      title='Module Manager'
      open={modalOpen}
      onClose={onClose}
    >
      <div style={{ display: 'flex' }}>
        <IconButton
          className={classes.expandButton}
          style={{
            transform: `rotate(${saveModuleExpanded ? '0' : '-90'}deg)`,
          }}
          onClick={() => setSaveModuleExpanded(!saveModuleExpanded)}
        >
          <ExpandMoreIcon />
        </IconButton>
        <Typography variant='subtitle1'>Create New Module</Typography>
      </div>
      <Collapse in={saveModuleExpanded}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid #e0e0e0',
            padding: '8px 8px 4px',
          }}
        >
          <TextField
            autoFocus={shouldCollapseGrid}
            label='Name'
            placeholder='Enter a name...'
            onChange={handleModuleNameInputChange}
            value={moduleNameInput}
            style={{ width: 346, height: 68, marginRight: 16 }}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              maxLength: 50,
            }}
            error={moduleNameError?.length > 0}
            helperText={moduleNameError}
          />
          <TextField
            label='Description'
            placeholder='Enter a description...'
            onChange={handleModuleDescriptionInputChange}
            value={moduleDescriptionInput}
            style={{ width: 346, height: 68, marginRight: 16 }}
            inputLabelProps={{
              shrink: true,
            }}
            error={descriptionExceedsLength}
            helperText={descriptionExceedsLength ? 'Length of description cannot exceed 100 characters' : ''}
          />
          <div style={{ flexGrow: 1, textAlign: 'end' }}>
            <Button
              variant='outlined'
              color='primary'
              onClick={onCreateNewModuleClick}
              style={{ margin: '0 8px' }}
              disabled={descriptionExceedsLength || moduleNameError?.length > 0}
            >
              Create
            </Button>
          </div>
        </div>
      </Collapse>
      <div className={classes.spacer} />
      <div style={{ display: 'flex' }}>
        <IconButton
          className={classes.expandButton}
          style={{
            transform: `rotate(${gridExpanded ? '0' : '-90'}deg)`,
            zIndex: 1,
          }}
          onClick={() => setGridExpanded(!gridExpanded)}
        >
          <ExpandMoreIcon />
        </IconButton>
        <Typography variant='subtitle1' style={{ zIndex: 1 }}>
          Saved Modules
        </Typography>
        {gridExpanded && (
          <IconButton
            className={classes.refreshButton}
            onClick={fetchAssumptionModules}
            style={{ zIndex: 1 }}
          >
            <RefreshIcon />
          </IconButton>
        )}
      </div>
      <Collapse in={gridExpanded}>
        {modulesAreLoading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <DataGrid
              id='moduleSavedFilterGrid'
              ref={gridRef}
              keyExpr={'moduleId'}
              persistState
              dataSource={
                modules?.length
                  ? modules.map((module) => {
                      return {
                        ...module,
                        canDelete:
                          user.roles.administrator,
                      };
                    })
                  : []
              }
              height={466}
              style={{ marginTop: -40 }}
            >
              <Column dataField={'name'} />
              <Column dataField={'description'} />
              <Column dataField={'createdBy'} />
              <Column
                dataField={'createdOn'}
                dataType={'date'}
                format={'MM/dd/yyyy'}
              />
              <Column dataField={'updatedBy'} />
              <Column
                dataField={'updatedOn'}
                dataType={'date'}
                format={'MM/dd/yyyy'}
              />
              <Column
                cellRender={deleteColumnRender}
                alignment='center'
                width={48}
              />
              <Paging defaultPageSize={pageSize} />
              <HeaderFilter visible={true} allowSearch={true} hideSelectAllOnSearch={false} />
              <SearchPanel visible={true} searchVisibleColumnsOnly={true} />
            </DataGrid>
          </>
        )}
      </Collapse>
    </ModalPage>
  );
}

const styles = (theme) => ({
  locationToggleContainer: {
    margin: '-44px 0px 1px 4px',
    display: 'flex',
    alignItems: 'center',
  },
  tooltipIcon: {
    zIndex: 1,
    fontSize: 16,
    marginLeft: 4,
  },
  footnote: {
    paddingTop: 10,
  },
  spacer: {
    height: 16,
  },
  savedFilterButtons: {
    fontSize: 12,
  },
  btnApply: {
    marginRight: 10,
    padding: 2,
    background: 'rgba(0, 0, 0, 0.1)',
    borderRadius: `5px`,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      cursor: `pointer`,
    },
  },
  refreshButton: {
    padding: 2,
    marginLeft: 6,
  },
  queryBuilder: {
    '& div.e-rule-field': {
      display: 'flex',
    },
    '& div.e-rule-filter': {
      width: '400px !important',
    },
    '& div.e-rule-value-delete': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  expandButton: {
    transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    padding: 2,
    marginRight: 6,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 426,
  },
});

function mapState({ assumptionModulesState, appState }) {
  return {
    user: appState.user,
    modules: assumptionModulesState.modules,
    modulesAreLoading: assumptionModulesState.modulesAreLoading,
  };
}

const mapDispatch = {
  fetchAssumptionModules,
  createAssumptionModule,
  deleteAssumptionModule,
  setConfirmationModalState,
};

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch)
)(ModuleManagerGrid);
